import { cn } from '@/libs/utils/cn';
import {
  COMPETITOR_HEATMAP_RAMP,
  MIN_HPC_SPOTS,
  STATION_CHARGE_CHIPS,
  TRAFFIC_COLORS,
} from '../constants';
import { PLACE_FILTER_CHIPS, placeChipMaki } from '../constants/placeSiteTypes';
import { ELECTRA_CHIP_ICON, LOW_HPC_CHIP_ICON, STATION_ICONS } from '../constants/stationIcons';
import type {
  LayerVisibilityHandlers,
  LayerVisibilityState,
} from '../hooks/useLayerVisibilityFilters';
import { poiLabelIcon } from '../utils/makiPaths';
import { placeMarkerColors } from '../utils/placeType';
import { ToggleChip } from './ToggleChip';
import { VisibilityToggleButton } from './VisibilityToggleButton';

export interface LayerVisibilityFiltersProps {
  state: LayerVisibilityState;
  handlers: LayerVisibilityHandlers;
}

function SvgSwatch({ svg }: { svg: string }) {
  return (
    <div
      className="size-5 flex-shrink-0 [&>svg]:size-full"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG from static assets
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

/** The same plate and glyph the basemap paints. `plate` false crops to the coloured disc. */
export function PlaceChipSwatch({
  maki,
  placeClass,
  className = 'size-5',
  plate = true,
}: {
  maki: string;
  placeClass: string;
  className?: string;
  plate?: boolean;
}) {
  const marker = placeMarkerColors(maki, placeClass);
  const icon = poiLabelIcon(maki);
  return (
    <svg viewBox="0 0 20 20" className={cn('shrink-0', className)} aria-hidden>
      {plate ? (
        <path
          d={icon.plate}
          fill={marker.background}
          stroke="#fff"
          strokeWidth={icon.plateStroke}
        />
      ) : (
        <circle cx="10" cy="10" r="10" fill={marker.background} />
      )}
      {icon.glyph ? (
        <path d={icon.glyph} fill={marker.foreground} transform={icon.transform ?? undefined} />
      ) : null}
    </svg>
  );
}

export function PlaceFilterChips({ state, handlers }: LayerVisibilityFiltersProps) {
  return (
    <div className="flex flex-row flex-wrap gap-2">
      {PLACE_FILTER_CHIPS.map((chip) => (
        <ToggleChip
          key={chip.id}
          label={chip.label}
          active={state.places[chip.id]}
          onToggle={() => handlers.togglePlace(chip.id)}
          swatch={<PlaceChipSwatch maki={placeChipMaki(chip)} placeClass={chip.placeClass} />}
        />
      ))}
    </div>
  );
}

export function TrafficFilterChips({ state, handlers }: LayerVisibilityFiltersProps) {
  return (
    <div className="flex flex-row flex-wrap gap-2">
      {Object.values(TRAFFIC_COLORS).map((item) => (
        <ToggleChip
          key={item.id}
          label={item.label}
          active={state.traffic[item.id]}
          onToggle={() => handlers.toggleTraffic(item.id)}
          swatch={
            <span
              className="size-2.5 rounded-full"
              style={{ backgroundColor: item.color }}
              aria-hidden
            />
          }
        />
      ))}
    </div>
  );
}

/** Electra stations, low-HPC stations, the charge bands, and the session clusters. */
export function CompetitorFilterOptions({ state, handlers }: LayerVisibilityFiltersProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row flex-wrap gap-2">
        <ToggleChip
          label="Electra stations"
          active={state.isElectraVisible}
          onToggle={handlers.toggleElectra}
          swatch={<SvgSwatch svg={ELECTRA_CHIP_ICON} />}
        />
        <ToggleChip
          label={`Stations with <${MIN_HPC_SPOTS} HPC`}
          active={state.isLowHpcVisible}
          onToggle={handlers.toggleLowHpc}
          swatch={<SvgSwatch svg={LOW_HPC_CHIP_ICON} />}
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Charges per station per day
        </span>
        <div className="h-px flex-1 bg-border" aria-hidden />
      </div>
      <div className="flex flex-row flex-wrap gap-2">
        {STATION_CHARGE_CHIPS.map((item) => (
          <ToggleChip
            key={item.id}
            label={item.label}
            active={state.station[item.label]}
            onToggle={() => handlers.toggleStation(item.label)}
            swatch={<SvgSwatch svg={STATION_ICONS[item.rangeKey].icon} />}
          />
        ))}
      </div>

      <div className={cn('flex flex-col gap-1', !state.isClustersVisible && 'opacity-50')}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Last quartile sessions per day
          </span>
          <div className="h-px flex-1 bg-border" aria-hidden />
          <VisibilityToggleButton
            visible={state.isClustersVisible}
            label="Toggle competitor clusters"
            onToggle={handlers.toggleClusters}
          />
        </div>
        <div
          className="h-2 w-full rounded-full"
          role="img"
          aria-label={`Last quartile sessions per day, from ${COMPETITOR_HEATMAP_RAMP[0].label} to ${COMPETITOR_HEATMAP_RAMP.at(-1)?.label}`}
          style={{
            background: `linear-gradient(to right, ${COMPETITOR_HEATMAP_RAMP[0].color} 0%, ${COMPETITOR_HEATMAP_RAMP.map((stop) => `${stop.color} ${stop.density * 100}%`).join(', ')})`,
          }}
        />
        <div className="relative h-3.5 text-[10px] text-muted-foreground">
          {COMPETITOR_HEATMAP_RAMP.map((stop, index) => {
            const atStart = index === 0;
            const atEnd = index === COMPETITOR_HEATMAP_RAMP.length - 1;
            return (
              <span
                key={stop.sessions}
                className={`absolute top-0 whitespace-nowrap ${atStart ? 'left-0' : atEnd ? 'right-0' : '-translate-x-1/2'}`}
                style={atStart || atEnd ? undefined : { left: `${stop.density * 100}%` }}
              >
                {stop.label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
